<template>
    <div>
        <v-card v-if="!justPlot">
            <v-card-title class="title accent white--text">
                Cooling Load
                <v-spacer></v-spacer>
                <date-range-selector v-model="range"></date-range-selector>
                <v-btn color="primary" fab small dark @click.prevent="$refs.explanationDialog.openDialog()">
                    <v-icon>fa-info</v-icon>
                </v-btn>
                <cooling-load-explanation ref="explanationDialog"></cooling-load-explanation>
            </v-card-title>
            <v-card-text class="caption grey--text">
                <v-layout>
                    <loading-progress-bar :loading="loading"></loading-progress-bar>
                    <v-spacer></v-spacer>
                    <span>{{facility_name}}</span>
                </v-layout>
            </v-card-text>
            <v-expand-transition mode="out-in">
            <v-card-text key=1 v-if="!loading">
              <vue-plotly :data="data" :layout="layout" :options="options"/>
            </v-card-text>
            </v-expand-transition>
        </v-card>
        <vue-plotly v-if="justPlot&&!loading" show-export :data="data" :layout="layout" :options="options"/>
    </div>

</template>

<script>
import VuePlotly from '@/components/VuePlotly'
import {getTimeSeries,callAPI} from '@/helper'
import LoadingProgressBar from './LoadingProgressBar'
import CoolingLoadExplanation from '@/components/CoolingLoadExplanation'
import DateRangeSelector from "./DateRangeSelector"
import moment from 'moment'


  export default {
    name: 'CoolingLoadStackedAreaPlot',
    components: {
      LoadingProgressBar,
      VuePlotly,
        CoolingLoadExplanation,
        DateRangeSelector
    },
    data: function () {
      return {
     data:[],
          timeout:null,
          colorMap:{
              "ACPC-1":"rgba(255, 127, 14,0.5)",
              "ACPC-2":"rgba(214, 39, 40,0.5)",
              "ACPC-3":"rgba(44, 160, 44, 0.5)",
              "ACPC-4":"rgba(31, 119, 180,0.5)",
              "ACPC-5": "rgba(148, 103, 189,0.5)",
              "ACPC-6": "rgba(140, 86, 75,0.5)",
              "ACPC-7": "rgba(227, 119, 194,0.5)",
              "ACPC-8": "rgba(127, 127, 127,0.5)",
              "ACPC-9": "rgba(188, 189, 34,0.5)",
              "Chiller1": "rgba(255, 127, 14,0.5)",
              "Chiller2": "rgba(214, 39, 40,0.5)",
              "Chiller3": "rgba(44, 160, 44, 0.5)",
              "Chiller4": "rgba(31, 119, 180,0.5)",
              "Chiller5": "rgba(148, 103, 189,0.5)",
              "Chiller6": "rgba(140, 86, 75,0.5)",
              "Chiller7": "rgba(227, 119, 194,0.5)",
              "Chiller1A": "rgba(255, 127, 14,0.5)",
              "Chiller1B": "rgba(188, 189, 34,0.5)",
          },
      options: {},
          range: this.dateRange? this.dateRange : {start:moment().subtract(30, "days").format("YYYY-MM-DD"),end:moment().format("YYYY-MM-DD")},
      loading: true
    }
    },
    props: {
      agg: {type:String, default: 'M'},
        dateRange: {type: Object, default: null},
        justPlot:{type:Boolean,default:false}
    },
      watch: {
          range () {
              this.refresh()
          },
          dateRange(){
              if(this.dateRange&&this.dateRange.start){
                  this.range=this.dateRange
                  this.refresh()
              }
          }
      },
    computed: {
      layout () {
        return {
          yaxis: {
            title: 'Cooling Load (KW)'
          },
            yaxis2: {
                title: 'Outdoor Temperature (°C)',
                overlaying: 'y',
                showgrid:false,
                side: 'right'
            },
          xaxis: {
            title: 'Time (hourly)'
          },
            legend: {
                orientation: 'h',
                y:1.2
            },
        }
      },
      facility_name () {
        return this.$store.state.pointsListStore.pointsList.facility_name
      },
        oatVar(){
            if(this.$store.state.pointsListStore.pointsList.facility_name==="CRP"){
                return 'Temperature';
            } else {
                return 'Outdoor_Temperature';
            }

        },
    },
      methods: {
          debounce(callback, wait) {
              clearTimeout(this.timeout);
              this.timeout = setTimeout(() => callback(), wait);
          },
          refresh() {
              this.debounce(this.doRefresh, 200);
          },
          async doRefresh() {
              this.loading = true
              let output = []
              if(!this.$store.state.pointsListStore.pointsList) {
                  return
              }
              for(let i in this.$store.state.pointsListStore.pointsList.equipment_details){
                  if (this.$store.state.pointsListStore.pointsList.equipment_details[i].equipment_type.toLowerCase().startsWith('chiller')){
                      let df = await getTimeSeries(['Cooling_Load'],
                          this.$store.state.pointsListStore.pointsList.equipment_details[i].equipment_name,
                          this.$store.state.pointsListStore.pointsList.facility_name,
                          this.range,
                          {agg:this.agg, agg_op:'sum'})
                      let res = df['Cooling_Load'].map(x => x>0? x/this.$store.state.pointsListStore.pointsList.sampling_freq:0)
                      let seg={
                          x: df['timestamp'],
                          y: res,
                          name: this.$store.state.pointsListStore.pointsList.equipment_details[i].equipment_name,
                          stackgroup: 'one',
                          type: 'scatter',
                          mode: 'none'
                      }
                      if(this.colorMap[this.$store.state.pointsListStore.pointsList.equipment_details[i].equipment_name]){
                          seg.fillcolor=this.colorMap[this.$store.state.pointsListStore.pointsList.equipment_details[i].equipment_name]
                      }
                      output.push(seg)
                  }
              }
              output.sort((a, b) => (a.name > b.name) ? -1 : 1)

              let weather = await callAPI('data/',
                  {
                      'facility_name': this.$store.state.pointsListStore.pointsList.facility_name,
                      'equipment_name': 'Weather',
                      'agg': this.agg,
                      'agg_op': 'mean',
                      'date_range':[this.range.start,this.range.end],
                      'sensor_names': [this.oatVar]
                  }
              )
              output.push({
                  x: weather.data.timestamp,
                  y: weather.data[this.oatVar],
                  name: "Outdoor Temperature",
                  type: 'scatter',
                  yaxis: 'y2',
                  mode: 'lines'
              })
              this.data = output
              this.loading = false
          }
      },
    async mounted () {
      this.refresh()
    },
      beforeMount() {
          let maxDate=this.$store.state.pointsListStore.pointsList.data_max_date;
          if(maxDate&&!this.dateRange){
              this.range={start:moment(maxDate).subtract(30, "days").format('YYYY-MM-DD'),end:moment(maxDate).format('YYYY-MM-DD')}
          }
      }
  }
</script>

<style scoped>

</style>
